import React, { useEffect, useState } from 'react';
import { Container, Row, Col, InputGroup } from 'react-bootstrap';
import useTranslation from 'next-translate/useTranslation';
import imageService from '@/services/image-service';
import { useGetProductByCodeQuery, useLazyGetUserPriceByCodeQuery } from '@/stores/productsApi';
import { AppShell } from '@components/AppShell';
import { ProductLabels } from '@/components/ProductLabels';
import { PriceDisplay } from '@/components/PriceDisplay';
import { ProductDownloads } from '@/components/ProductDownloads';
import { ProductMediaCarousel } from '@/components/ProductMediaCarousel';
import { ProductSymbols } from '@/components/ProductSymbols';
import { ProductAttributeSelector } from '@/components/ProductAttributeSelector';
import { UnitsOfMeasure } from '@/components/ProductAttributeSelector/UnitsOfMeasure';
import { ProductUSPs } from '@/components/ProductUSPs/ProductUSPs';
import { ProductTechnicalData } from '@/components/ProductTechnicalData/ProductTechnicalData';
import { ConnectedProductSpecialist } from '@/components/ProductSpecialist';
import * as Types from '@/pages/mijn-carel-lurvink/catalogus/stores/types';
import { getVimeoId, getVimeoUrls } from './getVimeoUrls';
import { getYoutubeUrls } from './getYoutubeUrls';
import styles from './ProductDetails.module.scss';
import richtextStyles from './richtext.module.scss';

interface IProductDetails {
  productCode: string;
  variantCode: string;
  unitOfMeasureCode: string;
  attributes?: Types.VariantAttributes;
  quantity?: number;
  selectableVariantAttributes?: Types.SelectableVariantAttribute[];
  onAddToCart?: AppCallbackFn;
  onAddToOrderList?: AppCallbackFn;
  onAttributeChange?: AppCallbackFn;
  onQuantityChange?: AppCallbackFn;
  onUnitOfMeasureChange?: AppCallbackFn;
  isAddToCartAllowed?: boolean;
  isAddToOrderListAllowed?: boolean;
  showPrice?: boolean;
  isAddToCartDisabled?: boolean;
}

export const ProductDetails = (props: IProductDetails) => {
  const { t } = useTranslation('product-details');
  const {
    productCode,
    variantCode,
    unitOfMeasureCode,
    attributes = [],
    quantity = 0,
    selectableVariantAttributes = [],
    onAddToCart = null,
    onAddToOrderList = null,
    onAttributeChange,
    onQuantityChange,
    onUnitOfMeasureChange,
    isAddToOrderListAllowed,
    isAddToCartAllowed,
    showPrice,
    isAddToCartDisabled
  } = props;

  /* eslint-disable prettier/prettier */
  const { data: product, isLoading: isProductLoading } = useGetProductByCodeQuery(productCode);

  const [fetchUserPrice, { isLoading: isUserPriceLoading, isFetching: isUserPriceFetching, isUninitialized: isUserPriceUninitialized }] =
    useLazyGetUserPriceByCodeQuery();
  /* eslint-enable */

  const { symbols = [], files = [] } = product ?? {};
  const variant = product?.variants.find((variant) => variant.code === variantCode);
  const unitOfMeasure = variant?.unitsOfMeasure.find((uom) => uom.code === unitOfMeasureCode);

  const [productMedia, setProductMedia] = useState<any[]>([]);

  const [userPrices, setUserPrices] = useState({
    exTax: {
      numeric: unitOfMeasure ? quantity * unitOfMeasure?.prices.current.exTax.numeric : 0,
      formatted: ''
    },
    incTax: {
      numeric: unitOfMeasure ? quantity * unitOfMeasure?.prices.current.incTax.numeric : 0,
      formatted: ''
    }
  });

  useEffect(() => {
    const getUserPrices = async () => {
      const userPrice = await fetchUserPrice({
        unitOfMeasureCode,
        variantCode
      }).unwrap();
      setUserPrices(userPrice.current);
    };
    showPrice && getUserPrices();
  }, [unitOfMeasureCode, variantCode, showPrice, fetchUserPrice]);

  useEffect(() => {
    const mediaImages = variant?.media ?? [];
    const mediaVideos = product?.videos ?? [];
    const mediaItems = [...mediaImages, ...mediaVideos];
    if (mediaItems.length === 0) {
      return;
    }

    const buildMediaItems = async () => {
      const nextMedia = (
        await Promise.all(
          mediaItems.map(async (item) => {
            if (item.src?.includes('youtu')) {
              const youtube = getYoutubeUrls(item.src);
              if (!youtube) {
                return null; // skip unresolvable YouTube URLs
              }

              return {
                embedUrl: youtube.embedUrl,
                original: youtube.thumbnailLarge,
                thumbnail: youtube.thumbnailSmall,
                thumbnailClass: 'image-gallery-thumbnail',
                renderItem: (galleryItem: { embedUrl: string }) => (
                  <div className="image-gallery-video">
                    <iframe
                      title="product video"
                      width="675"
                      height="480"
                      src={galleryItem.embedUrl}
                      style={{ border: 'none' }}
                      allowFullScreen
                    />
                  </div>
                )
              };
            }

            if (item.src?.includes('vimeo')) {
              const vimeoId = getVimeoId(item.src);
              const embedUrl = `https://player.vimeo.com/video/${vimeoId}`;
              const images = await getVimeoUrls(item.src);

              return {
                embedUrl,
                ...images,
                thumbnailClass: 'image-gallery-thumbnail',
                renderItem: (galleryItem: { embedUrl: string }) => (
                  <div className="image-gallery-video">
                    <iframe
                      title="product video"
                      width="675"
                      height="480"
                      src={galleryItem.embedUrl}
                      style={{ border: 'none' }}
                      allowFullScreen
                    />
                  </div>
                )
              };
            }

            return {
              original: imageService.getUrl(item.src, 512, 314, true),
              originalClass: 'image-gallery-image',
              thumbnail: imageService.getUrl(item.src, 80, 80, true),
              thumbnailClass: 'image-gallery-thumbnail'
            };
          })
        )
      ).filter(Boolean);
      setProductMedia(nextMedia);
    };
    buildMediaItems();
  }, [variant, product]);

  type IHandleAttributeChange = {
    type: string;
    attributeValue: Types.MappedAttribute;
  };
  const handleAttributeChange = ({ type, attributeValue }: IHandleAttributeChange) => {
    typeof onAttributeChange === 'function' &&
      onAttributeChange({
        type,
        attributeValue
      });
  };

  const handleAddToCart = () => {
    typeof onAddToCart === 'function' && onAddToCart();
  };

  const handleAddToOrderList = () => {
    typeof onAddToOrderList === 'function' && onAddToOrderList();
  };

  const pricesLoading = !isUserPriceLoading && !isUserPriceFetching && !isUserPriceUninitialized;
  if (isProductLoading) {
    return null;
  }
  return (
    <Container fluid>
      <Row>
        <Col xs={12} lg={7}>
          <AppShell.Container className="mb-3 p-relative">
            <div className={styles.variantCode}>
              <span className="text-muted">
                {t('variant-code')}
                {variant?.code}
              </span>
            </div>
            {product?.labels && (
              <div className={styles.labels}>
                <ProductLabels productLabels={product.labels} />
              </div>
            )}
            <ProductMediaCarousel mediaItems={productMedia} />
          </AppShell.Container>
        </Col>
        <Col xs={12} lg={5}>
          <AppShell.Container className="mb-3">
            {unitOfMeasure && showPrice && (
              <PriceDisplay
                price={userPrices}
                unitOfMeasure={unitOfMeasure}
                priceClassname={pricesLoading ? styles.price : styles.textContentLoading}
                descriptionClassname={styles.priceDescription}
              />
            )}
          </AppShell.Container>

          <AppShell.Container className="mb-3">
            {handleAttributeChange && attributes && selectableVariantAttributes && (
              <ProductAttributeSelector
                onAttributeChange={handleAttributeChange}
                // @ts-expect-error: wtf TS? `| never[]`
                attributes={attributes}
                selectableVariantAttributes={selectableVariantAttributes}
              />
            )}

            {onUnitOfMeasureChange && variant && unitOfMeasure && (
              <UnitsOfMeasure
                unitsOfMeasure={variant.unitsOfMeasure}
                currentUnitOfMeasure={unitOfMeasure}
                onUnitOfMeasureChange={onUnitOfMeasureChange}
              />
            )}
          </AppShell.Container>

          <AppShell.Container className="mb-3">
            {isAddToOrderListAllowed && onAddToOrderList && (
              <button className={'btn btn-outline-primary btn-block btn-fixed mb-3'} type="button" onClick={handleAddToOrderList}>
                <span className="fa cl-add-to-list fa-lg mr-3"></span>
                <span>{t('common:button.add-to-orderlist')}</span>
              </button>
            )}

            {isAddToCartAllowed && onAddToCart && onQuantityChange && (
              <InputGroup>
                <InputGroup.Prepend>
                  <input type="number" name="quantity" value={quantity} className={styles.amount} min="0" onChange={onQuantityChange} />
                </InputGroup.Prepend>
                <button
                  className={`${styles.btnCart} btn btn-primary`}
                  type="button"
                  disabled={isAddToCartDisabled}
                  onClick={handleAddToCart}
                >
                  <span className="fa cl-cart fa-lg mr-3"></span>
                  <span>{t('common:button.add-to-cart')}</span>
                </button>
              </InputGroup>
            )}
          </AppShell.Container>

          <AppShell.Container>
            <ConnectedProductSpecialist />
          </AppShell.Container>
        </Col>
        <Col xs={12} lg={7}>
          {/* @ts-expect-error: TS and optional chaining / nullish coalescing :( */}
          {product?.uniqueSellingPoints?.length > 0 && (
            <AppShell.Container className="mb-3 product-details-usp">
              {/* @ts-expect-error: :| */}
              <ProductUSPs uniqueSellingPoints={product.uniqueSellingPoints} />
            </AppShell.Container>
          )}

          {symbols?.length > 0 && (
            <AppShell.Container className="mb-3">
              <ProductSymbols symbols={symbols} />
            </AppShell.Container>
          )}

          <AppShell.Container className="mb-3">
            <h3 className="h2 mb-2">{t('title.description')}</h3>
            {product?.description && (
              <div
                className={`${styles.description} ${richtextStyles.richtext}`}
                dangerouslySetInnerHTML={{ __html: product.description }}
              ></div>
            )}
          </AppShell.Container>
          {product?.technicalData && <ProductTechnicalData productTechnicalData={product.technicalData} />}
        </Col>

        <Col xs={12} lg={5}>
          {files?.length > 0 && (
            <AppShell.Container className="mt-3">
              <ProductDownloads files={files} />
            </AppShell.Container>
          )}
        </Col>
      </Row>
    </Container>
  );
};
